import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { bgPrimary } from '@osmosis/tailwind-ui/styles';
import { faRotate } from '@fortawesome/pro-regular-svg-icons/faRotate';
import { faClone } from '@fortawesome/pro-regular-svg-icons/faClone';
import { useIntl } from 'react-intl';

import { VideoEnum } from '../../../../types/video-player';

type Props = {
  videoType: VideoEnum;
  isSearchBarStickable: boolean;
  onSync: () => void;
  onPictureInPicture: () => void;
};

const PILL_BUTTON_CLASS = cn(
  'flex items-center gap-2 !text-sm font-bold',
  'focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black',
  'active:bg-slate-300/20 dark:active:bg-slate-800',
  text0,
);

/**
 * Floating "Sync to video" / "Watch PiP" pill. An absolute overlay so it's out
 * of flow (can't push entries down); the inner wrapper is natively sticky and
 * clamps within the entries container.
 */
export default function SyncPill({
  videoType,
  isSearchBarStickable,
  onSync,
  onPictureInPicture,
}: Props) {
  const intl = useIntl();
  const isYouTube = videoType === VideoEnum.YOUTUBE;

  return (
    <Box className="absolute inset-0 z-10 pointer-events-none">
      <Flex
        data-testid="captions-sync-pill-wrapper"
        className={cn(
          'sticky justify-center mt-2',
          isSearchBarStickable ? 'top-[90px]' : 'top-14',
        )}
      >
        <Flex
          data-testid="captions-sync-pill"
          className={cn(
            'pointer-events-auto items-center rounded-full shadow-lg overflow-hidden',
            bgPrimary,
          )}
        >
          <Button
            variant="transparent"
            data-testid="captions-sync-pill-sync"
            onClick={onSync}
            cn={cn(
              isYouTube ? '!rounded-full' : '!rounded-l-full !rounded-r-none',
              PILL_BUTTON_CLASS,
            )}
          >
            <Icon icon={faRotate} size="1em" />
            {intl.formatMessage({
              id: 'learn.captions_transcript.sync_to_video',
              defaultMessage: 'Sync to video',
            })}
          </Button>
          {!isYouTube && (
            <>
              <Box className="w-px self-stretch bg-white/20" />
              <Button
                variant="transparent"
                data-testid="captions-sync-pill-pip"
                onClick={onPictureInPicture}
                cn={cn('!rounded-r-full !rounded-l-none', PILL_BUTTON_CLASS)}
              >
                <Icon icon={faClone} size="1em" />
                {intl.formatMessage({
                  id: 'learn.captions_transcript.watch_pip',
                  defaultMessage: 'Watch PiP',
                })}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
